import { Workflow } from 'lucide-react';
import { useState } from 'react';
import { Button } from '@/components/ui/button';
import CollectionStrategiesPage from '@/pages/settings/strategies';
import { StrategyEditorPage } from '@/pages/settings/strategy-editor';
import { ModeProvider } from '@/providers/ModeProvider';
import type { CollectionStrategyItem } from '@/types/collection-strategies';

type EditorState =
  | { open: false }
  | { open: true; mode: 'create'; strategy?: undefined }
  | { open: true; mode: 'edit'; strategy: CollectionStrategyItem };

export function Step5CollectionStrategies() {
  const [setupNow, setSetupNow] = useState(false);
  const [editorState, setEditorState] = useState<EditorState>({ open: false });

  const openCreate = () => setEditorState({ open: true, mode: 'create' });
  const openEdit = (strategy: CollectionStrategyItem) => setEditorState({ open: true, mode: 'edit', strategy });
  const closeEditor = () => setEditorState({ open: false });

  const handleStrategySaved = (_strategy: CollectionStrategyItem, _action: 'create' | 'edit') => {
    closeEditor();
  };

  return (
    <div className="flex flex-col h-full">
      {/* Step header */}
      {!editorState.open && (
        <div className="mb-6 shrink-0">
          <h2 className="text-xl font-semibold mb-1">Collection Strategies</h2>
          <p className="text-sm text-muted-foreground">Automate your collections with rule-based strategies.</p>
        </div>
      )}

      {/* Content */}
      <div className="flex-1 min-h-0 overflow-y-auto">
        {editorState.open ? (
          <ModeProvider>
            <StrategyEditorPage
              inlineMode={{
                mode: editorState.mode,
                strategy: editorState.strategy,
                onBack: closeEditor,
                onSaved: handleStrategySaved,
              }}
            />
          </ModeProvider>
        ) : setupNow ? (
          <ModeProvider>
            <CollectionStrategiesPage onCreateStrategy={openCreate} onEditStrategy={openEdit} />
          </ModeProvider>
        ) : (
          <div className="flex flex-col items-center justify-center h-full text-center">
            <div className="flex h-20 w-20 items-center justify-center rounded-2xl bg-primary/10 text-primary mb-6">
              <Workflow className="h-10 w-10" />
            </div>
            <h3 className="text-lg font-semibold mb-2">No collection strategies set up yet</h3>
            <p className="text-sm text-muted-foreground mb-6 max-w-sm">
              Strategies are <strong>not mandatory</strong> — you can set up the rest of Access Collect without them.
              When you're ready, they can also be configured from <strong>Settings → Collection Strategies</strong>.
            </p>
            <Button onClick={() => setSetupNow(true)} className="gap-2">
              <Workflow className="h-4 w-4" />
              Set Up Strategies Now
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
